import React, { useState, useEffect, useCallback, useRef } from 'react';

import * as Form from 'components/form';
import * as Page from 'components/page';
import { RHP } from 'providers';
import { useApi } from 'hooks';

export function CorporateStep5({ useRankingCorporate, t }) {
    const { vars, putUserRankingApplication } = useRankingCorporate;
    const { application } = vars;
    const { steps } = application;
    const api = useApi();

    const [congresses, setCongresses] = useState([]);
    const [caseId, setCaseId] = useState('');
    const [selectedCongress, setSelectedCongress] = useState('');
    const [cases, setCases] = useState(application?.cases || []);
    const [submitting, setSubmitting] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const [saving, setSaving] = useState(false);
    const isFirstRender = useRef(true);

    // Fetch available congresses
    useEffect(() => {
        api.get('/congresses').then((res) => {
            if (Array.isArray(res)) setCongresses(res);
        }).catch(() => {});
    }, []);

    // Auto-save whenever cases change (skip first render)
    const saveCases = useCallback(async (casesToSave) => {
        setSaving(true);
        try {
            const payload = {
                ...application,
                cases: casesToSave.map((c) => ({
                    congressId: c.congressId,
                    caseId: c.caseId,
                    caseName: c.caseName || null,
                })),
            };
            if (!payload.steps) payload.steps = {};
            if (casesToSave.length > 0) payload.steps.step5 = true;

            await putUserRankingApplication(payload);
            setLastSaved(new Date());
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    }, [application, putUserRankingApplication]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        saveCases(cases);
    }, [cases]);

    // Check for previous step conclusion
    if (!steps || steps.step4 !== true) {
        RHP.replace('/ranking/latam/corporate/');
        return null;
    }

    const addCase = () => {
        if (!caseId.trim() || !selectedCongress) return;
        const alreadyExists = cases.some(
            (c) => c.caseId === caseId.trim() && c.congressId === selectedCongress
        );
        if (alreadyExists) return;

        const congress = congresses.find((c) => c._id === selectedCongress);
        setCases([
            ...cases,
            {
                congressId: selectedCongress,
                congressName: congress?.name || '',
                caseId: caseId.trim(),
            },
        ]);
        setCaseId('');
    };

    const removeCase = (index) => {
        setCases(cases.filter((_, i) => i !== index));
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        setSubmitting(true);
        try {
            await saveCases(cases);
            RHP.push('/ranking/latam/corporate');
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const formatSavedDate = (date) => {
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };

    return (
        <Page.Page name="CorporateStep5" customBackText={t('glossary:backToRanking')}>
            <Page.Title>{t('CorporateStep5Cases.title')}</Page.Title>
            <Page.Descr>{t('CorporateStep5Cases.description')}</Page.Descr>

            <form onSubmit={onSubmit}>
                <p style={{ fontWeight: 500, marginBottom: 16 }}>
                    {t('CorporateStep5Cases.formInstruction')}
                </p>

                <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 8 }}>
                        {t('CorporateStep5Cases.caseIdLabel')}
                    </label>
                    <input
                        type="text"
                        value={caseId}
                        onChange={(e) => setCaseId(e.target.value)}
                        placeholder={t('CorporateStep5Cases.caseIdPlaceholder')}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            marginBottom: 8,
                        }}
                    />

                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 8 }}>
                        {t('CorporateStep5Cases.congressLabel')}
                    </label>
                    <select
                        value={selectedCongress}
                        onChange={(e) => setSelectedCongress(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            marginBottom: 12,
                        }}
                    >
                        <option value="">{t('CorporateStep5Cases.selectCongress')}</option>
                        {congresses.map((c) => (
                            <option key={c._id} value={c._id}>
                                {c.name}
                            </option>
                        ))}
                    </select>

                    <button
                        type="button"
                        onClick={addCase}
                        disabled={!caseId.trim() || !selectedCongress}
                        style={{
                            padding: '8px 16px',
                            background: '#007bff',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            opacity: !caseId.trim() || !selectedCongress ? 0.5 : 1,
                        }}
                    >
                        + {t('CorporateStep5Cases.addCase')}
                    </button>
                </div>

                <p style={{ fontSize: 14, color: '#666' }}>
                    {t('CorporateStep5Cases.noCase')}{' '}
                    <a href="https://100os.net/Congresso_Casos" target="_blank" rel="noopener noreferrer">
                        https://100os.net/Congresso_Casos
                    </a>
                </p>

                {cases.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                        <label style={{ fontWeight: 'bold', marginBottom: 8, display: 'block' }}>
                            {t('CorporateStep5Cases.submittedCases')}
                        </label>
                        {cases.map((c, index) => {
                            const congress = congresses.find((cg) => cg._id === c.congressId);
                            return (
                                <div
                                    key={index}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '8px 12px',
                                        border: '1px solid #eee',
                                        borderRadius: 4,
                                        marginBottom: 4,
                                    }}
                                >
                                    <span>
                                        <strong>{c.caseId}</strong>
                                        {c.caseName && ` - ${c.caseName}`}
                                        {' '}
                                        ({congress?.name || c.congressName || c.congressId})
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeCase(index)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#dc3545',
                                            cursor: 'pointer',
                                            fontSize: 18,
                                        }}
                                    >
                                        &times;
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                <Page.Ruler />

                <div style={{ opacity: 0.7, fontSize: '0.9em' }}>
                    <Page.Title style={{ fontSize: '1.4rem' }}>{t('CorporateStep5Cases.bonusTitle')}</Page.Title>
                    <Page.Descr>{t('CorporateStep5Cases.bonusDescription')}</Page.Descr>
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                    <Page.Buttn
                        style={{ width: '20%' }}
                        // @ts-ignore
                        to="/ranking/latam/corporate/step4"
                    >
                        <span className="icon far fa-long-arrow-left" aria-hidden="true" />
                        <span className="text">{t('glossary:back')}</span>
                    </Page.Buttn>

                    <div style={{ width: '80%' }}>
                        <Form.Submt
                            text={t('glossary:continue')}
                            disabled={submitting}
                        >
                            <span className="icon far fa-long-arrow-right" aria-hidden="true" />
                        </Form.Submt>
                    </div>
                </div>

                {(lastSaved || saving) && (
                    <p style={{ fontSize: 12, color: '#aaa', textAlign: 'center', marginTop: 12 }}>
                        {saving
                            ? 'Salvando...'
                            : `Salvo automaticamente em ${formatSavedDate(lastSaved)}`}
                    </p>
                )}
            </form>
        </Page.Page>
    );
}
